// The stash of an open Item form (phase 2): before a navigation that stays
// in the form's draft scope (a variant tab, the property and its variant
// pages), the form puts its changes into the draft instead of asking "exit
// without saving?". The Item registers it with draft.registerOpenForm(...,
// { scope, stash }); useConfirmNavigation runs draft.stashOpenForms(). See
// README.md, "Open forms and the tab switch".
//
// Pure: the store and the form state are passed in.

import cloneDeep from "lodash.clonedeep";
import isEqual from "lodash.isequal";

// Fields of a row that the server owns (lastSaved, version: it changes them
// at every save) or that the client computes on the MemoryDB row
// (refsComputed): always the item's, never the form's. refs.belongs too.
const FROM_ITEM = ["lastSaved", "version", "refsComputed"];

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

// row[key] = source[key], or no key when the source has none (a key set to
// undefined makes two rows differ for isEqual).
function setOrDelete(row, key, source) {
  if (isObject(source) && key in source) row[key] = cloneDeep(source[key]);
  else delete row[key];
}

// The row a form saves (submit(clone(values)): what its Save sends), made
// comparable with the rows of the draft:
// - what submit derives (e.g. refs.require, doBlockRefs) is the reference
//   row's own value when submit derives the same from it: a field of the
//   form equal to the stored row's gives the stored value back, so that a
//   change undone leaves no draft (the store compares rows strictly), and a
//   stored row whose derived fields are in an older shape (refs.require
//   without a type, in another order) round-trips unchanged;
// - the server-owned fields (lastSaved, version, refs.belongs) and
//   refsComputed are the item's: the form's copy may be stale, or carry the
//   dirtiness hack of the variant page (lastSaved.date).
// - what the item adds to the row it is derived from (derive, e.g. the _id
//   of the sub-blocks of a block, itemRow.js) is derived the same way: a
//   field equal to the derived reference's gives the reference's own value
//   back, without what derive added.
// references: stored first (the row the draft started from: undo), then the
// item (the drafted row the form was opened from: a re-stash of an
// unchanged form is a no-op).
// submit(values) => row: the Item's onSubmit (identity when none).
// derive(row) => item: what the Item derives from a stored row (useItem's
// fromRow; identity when none). Not applied to the references themselves.
export function formRow({ values, item, stored, submit, derive }) {
  const finish = submit ?? ((row) => row);
  const row = finish(cloneDeep(values)) ?? cloneDeep(values);

  const references = [stored, item].filter(isObject).map((reference) => {
    const base = derive
      ? (derive(cloneDeep(reference)) ?? cloneDeep(reference))
      : cloneDeep(reference);
    return { reference, derived: finish(base) ?? base };
  });

  const keys = new Set(Object.keys(row));
  for (const { reference } of references)
    Object.keys(reference).forEach((key) => keys.add(key));

  for (const key of keys) {
    if (FROM_ITEM.includes(key) || key === "refs") continue;
    const same = references.find(({ derived }) =>
      isEqual(row[key], derived[key])
    );
    if (same) setOrDelete(row, key, same.reference);
  }

  // refs.require (refs.belongs is the server's, below)
  const same = references.find(({ derived }) =>
    isEqual(row.refs?.require, derived.refs?.require)
  );
  if (same) {
    const refs = same.reference.refs;
    if (isObject(refs) && "require" in refs)
      row.refs = { ...(row.refs ?? {}), require: cloneDeep(refs.require) };
    else if (isObject(row.refs)) delete row.refs.require;
  }

  for (const key of FROM_ITEM) setOrDelete(row, key, item);

  if (isObject(item?.refs) && "belongs" in item.refs)
    row.refs = { ...(row.refs ?? {}), belongs: cloneDeep(item.refs.belongs) };
  else if (isObject(row.refs)) delete row.refs.belongs;

  // No refs left: none, as the row whose refs.require was kept (or the
  // item) has none
  const shape = same?.reference ?? item;
  if (
    isObject(row.refs) &&
    Object.keys(row.refs).length === 0 &&
    !(isObject(shape) && "refs" in shape)
  )
    delete row.refs;

  return row;
}

const hasErrors = (errors) => isObject(errors) && Object.keys(errors).length > 0;

// The stash of an open form: "none" | "invalid" | "stashed", synchronously.
// A change the store refuses (a row held by another scope, a row open in
// another form: the endpoint params being typed, useOpenFormWhile) THROWS
// the store's error: draft.stashOpenForms counts it as "refused" and keeps
// the error, which useConfirmNavigation shows (it names the row).
//
// - "none": the form is clean (dirty: the Item's notion, props.dirty or
//   Formik's) and the page has no extra changes (extra(values) => []);
// - "invalid": it can't be put into the draft (validation errors, an edit
//   of a conditions box not handed over yet, an id renamed, main): the
//   navigation asks as before;
//   invalid: whether the errors stop the stash (default: any error). The
//   Item lets a row created in the draft through with the errors its page
//   accepts (e.g. a new variant with no layout yet, see Item's
//   stashWithErrors): its Save stays refused while they are there;
// - "stashed": draft.change(scope, [the form row, ...extra(values)],
//   { closing: release }) committed: the form row and the page's extra rows
//   are in the draft, all or none.
//
// release: the EXACT release registerOpenForm returned for this form (the
// store frees the row only for it). extra(values) => ops: the page's rows
// changed with the form (e.g. the endpoint of a variant page). id: the
// stored id of the form's row (prevItemId). derive: see formRow.
export function stashForm({
  store,
  scope,
  accessor,
  id,
  release,
  values,
  item,
  stored,
  submit,
  derive,
  dirty = false,
  errors,
  invalid = hasErrors(errors),
  pending = false,
  renamed = false,
  onMain = false,
  extra
}) {
  if (!values) return "none";

  const extraOps = (extra ? extra(values) : null) ?? [];
  if (!dirty && extraOps.length === 0) return "none";
  // Nothing is drafted on main: ask, as before (Save is not possible there)
  if (invalid || pending || renamed || onMain) return "invalid";

  const ops = [];
  if (dirty) {
    const row = formRow({ values, item, stored, submit, derive });
    ops.push({ kind: "edit", accessor, id, updater: () => row });
  }
  ops.push(...extraOps);

  // Throws when refused (see above); false on main
  const rows = store.change(scope, ops, { closing: release });
  return rows === false ? "invalid" : "stashed";
}
